/**
 * Bundle domain schemas — consolidation bundle records for TMS-CONSOLIDATION-SURFACE-1.
 * Bundles group multiple LTL loads into a single FTL shipment recommended by KCON.
 */
import { z } from 'zod';
import { TenantIdSchema, IsoDateSchema } from './_primitives.js';

// ── Enums ─────────────────────────────────────────────────────────────────────

export const BUNDLE_STATUS = Object.freeze({
  RECOMMENDED: 'RECOMMENDED',
  APPROVED:    'APPROVED',
  SHIPPED:     'SHIPPED',
  INVOICED:    'INVOICED',
});

export const CONSOLIDATION_STATUS = Object.freeze({
  PENDING:  'PENDING',
  BUNDLED:  'BUNDLED',
  SHIPPED:  'SHIPPED',
});

export const BundleStatusEnum         = z.enum(Object.values(BUNDLE_STATUS));
export const ConsolidationStatusEnum  = z.enum(Object.values(CONSOLIDATION_STATUS));

// ── Projected cost sub-schema ─────────────────────────────────────────────────

const ProjectedCostSchema = z.object({
  ltlCost:  z.number().nonnegative(),
  ftlCost:  z.number().nonnegative(),
  savings:  z.number(),
}).optional();

// ── Root Bundle schema ────────────────────────────────────────────────────────

export const BundleSchema = z.object({
  // Identity
  bundleId:         z.string().min(1),
  tenantId:         TenantIdSchema,

  // Loads grouped into this bundle
  loadIds:          z.array(z.string().min(1)),

  // Status lifecycle: RECOMMENDED → APPROVED → SHIPPED → INVOICED
  bundleStatus:     BundleStatusEnum.default('RECOMMENDED'),

  // Cargo totals (from KCON payload)
  totalWeight:      z.number().nonnegative(),
  totalVolume:      z.number().nonnegative().optional(),
  utilizationRatio: z.number().min(0).max(1),

  // Cost projection from KCON
  projectedCost:    ProjectedCostSchema,

  // Approval trail
  approvedAt:       z.union([IsoDateSchema, z.object({})]).nullable().optional(),
  approvedBy:       z.string().nullable().optional(),

  // Source — 'kcon' for real substrate, 'stub' for fallback
  source:           z.enum(['kcon', 'stub']).default('kcon'),

  // Soft delete — never hard-delete per platform doctrine
  softDeleted:      z.boolean().default(false),

  // Metadata
  createdAt:        z.union([IsoDateSchema, z.object({})]).optional(),
  updatedAt:        z.union([IsoDateSchema, z.object({})]).optional(),
}).passthrough();
